// @ts-nocheck
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { getAuthToken, getAuthUser } from '../../utils/auth';
import { API_ENDPOINTS } from '../../config/api';
import './Reader.css';

import ReaderSidebar from './components/ReaderSidebar';
import ReaderContentList from './components/ReaderContentList';
import ReaderDetailPane from './components/ReaderDetailPane';
import AddLinkModal from './components/AddLinkModal';
import EditLinkModal from './components/EditLinkModal';
import AddFeedModal from './components/AddFeedModal';
import ManageFeedsModal from './components/ManageFeedsModal';
import CreateCollectionModal from './components/CreateCollectionModal';
import ReaderSettingsModal from './components/ReaderSettingsModal';

export default function Reader() {
  const navigate = useNavigate();
  const token = getAuthToken();
  const user = getAuthUser();

  // Primary Data State
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [feeds, setFeeds] = useState([]);
  const [collections, setCollections] = useState([]);
  const [tags, setTags] = useState([]);
  const [stats, setStats] = useState({ unreadCount: 0, totalSaved: 0, archivedCount: 0, totalFeeds: 0 });

  // Navigation & Filter State
  const [currentView, setCurrentView] = useState('inbox'); // 'inbox', 'library', 'favorites', 'archive'
  const [selectedFeedId, setSelectedFeedId] = useState(null);
  const [selectedCollectionId, setSelectedCollectionId] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all'); // 'all', 'article', 'video', 'podcast', 'social'
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest', 'oldest'
  const [searchQuery, setSearchQuery] = useState('');

  // Layout & Responsive State
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isMobileReading, setIsMobileReading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Modals
  const [isAddLinkOpen, setIsAddLinkOpen] = useState(false);
  const [isEditLinkOpen, setIsEditLinkOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isAddFeedOpen, setIsAddFeedOpen] = useState(false);
  const [isManageFeedsOpen, setIsManageFeedsOpen] = useState(false);
  const [isCreateCollectionOpen, setIsCreateCollectionOpen] = useState(false);
  const [justCreatedCollectionId, setJustCreatedCollectionId] = useState<string | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Auto-delete execution guards to prevent re-fetch loops
  const hasAutoDeletedRef = useRef(false);
  const isAutoDeletingRef = useRef(false);

  // Auto-Delete Settings State
  const [autoDeleteSettings, setAutoDeleteSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('reader_auto_delete_settings');
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error('[Reader] Failed to load auto-delete settings:', e);
    }
    return { enabled: false, target: 'unread', olderThanDays: 3 };
  });

  const handleUpdateAutoDeleteSettings = (newSettings) => {
    setAutoDeleteSettings(newSettings);
    try {
      localStorage.setItem('reader_auto_delete_settings', JSON.stringify(newSettings));
    } catch (e) {
      console.error('[Reader] Failed to persist auto-delete settings:', e);
    }
  };

  // Authenticated API request helper
  const authFetch = useCallback(
    async (url, options = {}) => {
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      };
      const res = await fetch(url, { ...options, headers });
      if (res.status === 401) {
        navigate('/login?redirect=/app-store/reader');
        throw new Error('Authentication required');
      }
      return res;
    },
    [token, navigate]
  );

  // 1. Fetch Stats & Feeds & Collections & Tags
  const fetchMetadata = useCallback(async () => {
    if (!token) return;
    try {
      const [statsRes, feedsRes, colRes, tagsRes] = await Promise.all([
        authFetch(API_ENDPOINTS.READER.STATS),
        authFetch(API_ENDPOINTS.READER.FEEDS),
        authFetch(API_ENDPOINTS.READER.COLLECTIONS),
        authFetch(API_ENDPOINTS.READER.TAGS),
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        if (statsData.success) setStats(statsData.data);
      }
      if (feedsRes.ok) {
        const feedsData = await feedsRes.json();
        if (feedsData.success) setFeeds(feedsData.data);
      }
      if (colRes.ok) {
        const colData = await colRes.json();
        if (colData.success) setCollections(colData.data);
      }
      if (tagsRes.ok) {
        const tagsData = await tagsRes.json();
        if (tagsData.success) setTags(tagsData.data);
      }
    } catch (err) {
      console.error('[Reader] fetchMetadata error:', err);
    }
  }, [authFetch, token]);

  // 2. Fetch Library Items based on current selection
  const fetchItems = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams();

      if (currentView === 'inbox') {
        params.set('readState', 'unread');
        params.set('savedState', 'saved');
      } else if (currentView === 'library') {
        params.set('savedState', 'saved');
      } else if (currentView === 'favorites') {
        params.set('isFavorite', 'true');
        params.set('savedState', 'saved');
      } else if (currentView === 'archive') {
        params.set('savedState', 'archived');
      }

      if (selectedFeedId) params.set('feedId', selectedFeedId);
      if (selectedCollectionId) params.set('collectionId', selectedCollectionId);
      if (selectedTag) params.set('tag', selectedTag);
      if (selectedFilter !== 'all') params.set('sourceType', selectedFilter);
      if (sortOrder) params.set('sort', sortOrder);
      if (searchQuery.trim()) params.set('search', searchQuery.trim());
      params.set('limit', '100');

      const res = await authFetch(`${API_ENDPOINTS.READER.ITEMS}?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setItems(data.data || []);
          // Preserve selected item if still in list, otherwise select first item on desktop
          if (data.data && data.data.length > 0) {
            setSelectedItem((prev) => {
              if (prev) {
                const found = data.data.find((i) => i._id === prev._id);
                if (found) return found;
              }
              // Only auto-select first item on wide desktop screens
              return window.innerWidth >= 1024 ? data.data[0] : null;
            });
          } else {
            setSelectedItem(null);
          }
        }
      }
    } catch (err) {
      console.error('[Reader] fetchItems error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [
    authFetch,
    token,
    currentView,
    selectedFeedId,
    selectedCollectionId,
    selectedTag,
    selectedFilter,
    sortOrder,
    searchQuery,
  ]);

  // Initial load
  useEffect(() => {
    if (!token) {
      navigate('/login?redirect=/app-store/reader');
      return;
    }
    fetchMetadata();
  }, [token, fetchMetadata, navigate]);

  // Trigger items fetch on filter/query changes
  useEffect(() => {
    if (!token) return;
    const debounce = setTimeout(() => {
      fetchItems();
    }, 200);
    return () => clearTimeout(debounce);
  }, [token, fetchItems]);

  // Handle Item Selection
  const handleSelectItem = (item) => {
    setSelectedItem(item);
    setIsMobileReading(true);

    // Auto mark as read on view if unread
    if (item.readState === 'unread') {
      handleToggleRead(item, 'read');
    }
  };

  // Toggle Read State
  const handleToggleRead = async (item, targetState) => {
    const newState = targetState || (item.readState === 'read' ? 'unread' : 'read');

    // Optimistic update
    setItems((prev) =>
      prev.map((i) => (i._id === item._id ? { ...i, readState: newState } : i))
    );
    if (selectedItem && selectedItem._id === item._id) {
      setSelectedItem((prev) => ({ ...prev, readState: newState }));
    }

    try {
      await authFetch(API_ENDPOINTS.READER.MARK_READ(item._id), {
        method: 'POST',
        body: JSON.stringify({ readState: newState }),
      });
      fetchMetadata();
    } catch (err) {
      console.error('[Reader] toggleRead error:', err);
    }
  };

  // Toggle Favorite State
  const handleToggleFavorite = async (item) => {
    const newFav = !item.isFavorite;

    // Optimistic update
    setItems((prev) =>
      prev.map((i) => (i._id === item._id ? { ...i, isFavorite: newFav } : i))
    );
    if (selectedItem && selectedItem._id === item._id) {
      setSelectedItem((prev) => ({ ...prev, isFavorite: newFav }));
    }

    try {
      await authFetch(API_ENDPOINTS.READER.ITEM(item._id), {
        method: 'PATCH',
        body: JSON.stringify({ isFavorite: newFav }),
      });
    } catch (err) {
      console.error('[Reader] toggleFavorite error:', err);
    }
  };

  // Toggle Archive State
  const handleToggleArchive = async (item) => {
    const newSaved = item.savedState === 'archived' ? 'saved' : 'archived';

    // Optimistic update
    setItems((prev) =>
      prev.map((i) => (i._id === item._id ? { ...i, savedState: newSaved } : i))
    );
    if (selectedItem && selectedItem._id === item._id) {
      setSelectedItem((prev) => ({ ...prev, savedState: newSaved }));
    }

    try {
      await authFetch(API_ENDPOINTS.READER.ARCHIVE(item._id), {
        method: 'POST',
        body: JSON.stringify({ savedState: newSaved }),
      });
      fetchItems();
      fetchMetadata();
    } catch (err) {
      console.error('[Reader] toggleArchive error:', err);
    }
  };

  // Dismiss Item from current view (archives it)
  const handleDismissItem = async (item) => {
    // Optimistically remove from current list
    setItems((prev) => prev.filter((i) => i._id !== item._id));
    if (selectedItem && selectedItem._id === item._id) {
      setSelectedItem(null);
      setIsMobileReading(false);
    }

    try {
      await authFetch(API_ENDPOINTS.READER.ARCHIVE(item._id), {
        method: 'POST',
        body: JSON.stringify({ savedState: 'archived' }),
      });
      fetchMetadata();
    } catch (err) {
      console.error('[Reader] dismissItem error:', err);
      fetchItems();
    }
  };

  // Delete Item
  const handleDeleteItem = async (item) => {
    setItems((prev) => prev.filter((i) => i._id !== item._id));
    if (selectedItem && selectedItem._id === item._id) {
      setSelectedItem(null);
      setIsMobileReading(false);
    }

    try {
      await authFetch(API_ENDPOINTS.READER.ITEM(item._id), {
        method: 'DELETE',
      });
      fetchMetadata();
    } catch (err) {
      console.error('[Reader] deleteItem error:', err);
    }
  };

  // Batch Delete Items
  const handleDeleteBatch = async (itemsToDelete) => {
    if (!itemsToDelete || itemsToDelete.length === 0) return;
    const idsToDelete = new Set(itemsToDelete.map((i) => i._id));

    setItems((prev) => prev.filter((i) => !idsToDelete.has(i._id)));
    if (selectedItem && idsToDelete.has(selectedItem._id)) {
      setSelectedItem(null);
      setIsMobileReading(false);
    }

    try {
      await Promise.all(
        itemsToDelete.map((item) =>
          authFetch(API_ENDPOINTS.READER.ITEM(item._id), {
            method: 'DELETE',
          }).catch((e) => console.error('[Reader] batch delete item error:', e))
        )
      );
      fetchMetadata();
    } catch (err) {
      console.error('[Reader] deleteBatch error:', err);
    }
  };

  // Run Auto-Delete across the full database (all pages + orphaned feed items)
  const handleRunAutoDelete = async (customSettings = autoDeleteSettings) => {
    if (isAutoDeletingRef.current) return { count: 0 };
    isAutoDeletingRef.current = true;

    try {
      // 1. Try server-side auto-delete endpoint first
      try {
        const autoDelRes = await authFetch(API_ENDPOINTS.READER.AUTO_DELETE, {
          method: 'POST',
          body: JSON.stringify({
            target: customSettings.target || 'unread',
            olderThanDays: customSettings.olderThanDays || 3,
            cleanOrphaned: customSettings.cleanOrphaned !== false,
          }),
        });
        if (autoDelRes.ok) {
          const autoDelData = await autoDelRes.json();
          if (autoDelData.success) {
            const count = autoDelData.data?.deletedCount || 0;
            if (count > 0) {
              await fetchMetadata();
              await fetchItems();
            }
            return { count };
          }
        }
      } catch (err) {
        console.warn('[Reader] Server-side auto-delete unavailable, falling back to multi-page scan:', err);
      }

      // 2. Client-side multi-page database scanner & deletion
      const now = Date.now();
      const thresholdMs = (customSettings.olderThanDays || 3) * 24 * 60 * 60 * 1000;
      const cutoffTime = now - thresholdMs;

      // Active feed IDs set
      const activeFeedIdSet = new Set((feeds || []).map((f) => String(f._id)));
      const cleanOrphaned = customSettings.cleanOrphaned !== false;

      let allMatchingItems = [];
      let page = 1;
      let totalPages = 1;

      // Query parameters for fetching all library items
      const baseParams = new URLSearchParams();
      baseParams.set('savedState', 'saved');
      baseParams.set('limit', '100');
      baseParams.set('sort', 'oldest'); // Oldest first to catch older articles

      if (customSettings.target === 'unread') {
        baseParams.set('readState', 'unread');
      }

      // Fetch across pages (up to 10 pages / 1,000 items)
      while (page <= totalPages && page <= 10) {
        const queryParams = new URLSearchParams(baseParams);
        queryParams.set('page', String(page));

        const res = await authFetch(`${API_ENDPOINTS.READER.ITEMS}?${queryParams.toString()}`);
        if (!res.ok) break;

        const resData = await res.json();
        if (!resData.success || !Array.isArray(resData.data)) break;

        const pageItems = resData.data;
        totalPages = resData.pagination?.totalPages || 1;

        for (const item of pageItems) {
          // Never delete favorite articles
          if (item.isFavorite) continue;

          // Check if article is from an unsubscribed/deleted feed
          const isFromDeletedFeed =
            item.feedId && !activeFeedIdSet.has(String(item.feedId));

          // Check age
          const dateStr = item.publishedAt || item.createdAt;
          const itemTime = dateStr ? new Date(dateStr).getTime() : 0;
          const isOld = itemTime > 0 && itemTime <= cutoffTime;

          if (isOld || (cleanOrphaned && isFromDeletedFeed)) {
            allMatchingItems.push(item);
          }
        }

        page++;
      }

      if (allMatchingItems.length === 0) {
        return { count: 0 };
      }

      // Deduplicate by ID
      const uniqueItemsMap = new Map();
      allMatchingItems.forEach((i) => uniqueItemsMap.set(i._id, i));
      const itemsToDelete = Array.from(uniqueItemsMap.values());

      // Delete in parallel chunks of 10
      const chunkSize = 10;
      for (let i = 0; i < itemsToDelete.length; i += chunkSize) {
        const chunk = itemsToDelete.slice(i, i + chunkSize);
        await Promise.allSettled(
          chunk.map((item) =>
            authFetch(API_ENDPOINTS.READER.ITEM(item._id), {
              method: 'DELETE',
            })
          )
        );
      }

      // Optimistically update items in state
      const deletedIds = new Set(itemsToDelete.map((i) => i._id));
      setItems((prev) => prev.filter((i) => !deletedIds.has(i._id)));
      if (selectedItem && deletedIds.has(selectedItem._id)) {
        setSelectedItem(null);
        setIsMobileReading(false);
      }

      // Refresh metadata and items to update sidebar badges
      await fetchMetadata();
      await fetchItems();

      return { count: itemsToDelete.length };
    } catch (err) {
      console.error('[Reader] Auto-delete error:', err);
      return { count: 0, error: err.message };
    } finally {
      isAutoDeletingRef.current = false;
    }
  };

  // Run auto-delete on load when enabled (only once per mount/session)
  useEffect(() => {
    if (!token || !autoDeleteSettings?.enabled || hasAutoDeletedRef.current) return;
    hasAutoDeletedRef.current = true;

    const timer = setTimeout(() => {
      handleRunAutoDelete(autoDeleteSettings);
    }, 2000);

    return () => clearTimeout(timer);
  }, [token, autoDeleteSettings?.enabled]);

  // Refresh Item (re-extract metadata and article content)
  const handleRefreshItem = async (item) => {
    try {
      const res = await authFetch(API_ENDPOINTS.READER.REFRESH_ITEM(item._id), {
        method: 'POST',
      });
      if (res && res.data) {
        setItems((prev) =>
          prev.map((i) => (i._id === item._id ? res.data : i))
        );
        if (selectedItem && selectedItem._id === item._id) {
          setSelectedItem(res.data);
        }
      }
    } catch (err) {
      console.error('[Reader] refreshItem error:', err);
    }
  };

  // Update Note
  const handleUpdateNote = async (item, note) => {
    try {
      await authFetch(API_ENDPOINTS.READER.ITEM(item._id), {
        method: 'PATCH',
        body: JSON.stringify({ note }),
      });
      setItems((prev) =>
        prev.map((i) => (i._id === item._id ? { ...i, note } : i))
      );
    } catch (err) {
      console.error('[Reader] updateNote error:', err);
    }
  };

  // Update Tags
  const handleUpdateTags = async (item, tagsList) => {
    try {
      await authFetch(API_ENDPOINTS.READER.ITEM(item._id), {
        method: 'PATCH',
        body: JSON.stringify({ tags: tagsList }),
      });
      setItems((prev) =>
        prev.map((i) => (i._id === item._id ? { ...i, tags: tagsList } : i))
      );
      if (selectedItem && selectedItem._id === item._id) {
        setSelectedItem((prev) => ({ ...prev, tags: tagsList }));
      }
      fetchMetadata();
    } catch (err) {
      console.error('[Reader] updateTags error:', err);
    }
  };

  // Update Collection
  const handleUpdateCollections = async (item, collectionIds) => {
    try {
      await authFetch(API_ENDPOINTS.READER.ITEM(item._id), {
        method: 'PATCH',
        body: JSON.stringify({ collectionIds }),
      });
      setItems((prev) =>
        prev.map((i) => (i._id === item._id ? { ...i, collectionIds } : i))
      );
      if (selectedItem && selectedItem._id === item._id) {
        setSelectedItem((prev) => ({ ...prev, collectionIds }));
      }
      fetchMetadata();
    } catch (err) {
      console.error('[Reader] updateCollections error:', err);
    }
  };

  // Open Edit Link Modal
  const handleOpenEditLink = (item) => {
    setEditingItem(item);
    setIsEditLinkOpen(true);
  };

  // Save Edit Link changes (including moving to one or multiple collections)
  const handleSaveEditLink = async (item, updates) => {
    try {
      const res = await authFetch(API_ENDPOINTS.READER.ITEM(item._id), {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error?.message || 'Failed to update item');
      }
      const updated = data.data;
      setItems((prev) => prev.map((i) => (i._id === item._id ? updated : i)));
      if (selectedItem && selectedItem._id === item._id) {
        setSelectedItem(updated);
      }
      fetchMetadata();
      if (selectedCollectionId) {
        fetchItems();
      }
      return updated;
    } catch (err) {
      console.error('[Reader] saveEditLink error:', err);
      throw err;
    }
  };

  // Save Link Dialog Submit
  const handleSaveLink = async (formData) => {
    const res = await authFetch(API_ENDPOINTS.READER.IMPORT, {
      method: 'POST',
      body: JSON.stringify(formData),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.error?.message || 'Failed to save link');
    }
    fetchItems();
    fetchMetadata();
    if (data.data) {
      setSelectedItem(data.data);
      setIsMobileReading(true);
    }
    return data;
  };

  // Update Feed Collections
  const handleUpdateFeedCollections = async (feedId, collectionIds) => {
    try {
      await authFetch(API_ENDPOINTS.READER.FEED(feedId), {
        method: 'PATCH',
        body: JSON.stringify({ collectionIds }),
      });
      setFeeds((prev) =>
        prev.map((f) => (f._id === feedId ? { ...f, collectionIds } : f))
      );
      fetchMetadata();
      fetchItems();
    } catch (err) {
      console.error('[Reader] updateFeedCollections error:', err);
    }
  };

  // Subscribe Feed Dialog Submit
  const handleSubscribeFeed = async (formData) => {
    const res = await authFetch(API_ENDPOINTS.READER.FEEDS, {
      method: 'POST',
      body: JSON.stringify(formData),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.error?.message || 'Failed to subscribe');
    }
    fetchMetadata();
    fetchItems();
    return data;
  };

  // Feed Discovery helper
  const handleDiscoverFeeds = async (url) => {
    const res = await authFetch(API_ENDPOINTS.READER.DISCOVER_FEEDS, {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.error?.message || 'Feed discovery failed');
    }
    return data.data || [];
  };

  // Manual Feed Refresh
  const handleRefreshFeed = async (feedId) => {
    const res = await authFetch(API_ENDPOINTS.READER.FEED_REFRESH(feedId), {
      method: 'POST',
    });
    const data = await res.json();
    fetchMetadata();
    fetchItems();
    return data;
  };

  // Refresh current feed (or all feeds) to fetch latest new articles
  const handleRefreshCurrentFeed = async () => {
    try {
      if (selectedFeedId) {
        await handleRefreshFeed(selectedFeedId);
      } else if (feeds && feeds.length > 0) {
        await Promise.allSettled(
          feeds.map((feed) =>
            authFetch(API_ENDPOINTS.READER.FEED_REFRESH(feed._id), {
              method: 'POST',
            })
          )
        );
        fetchMetadata();
        fetchItems();
      } else {
        await fetchItems();
        fetchMetadata();
      }
    } catch (err) {
      console.error('[Reader] handleRefreshCurrentFeed error:', err);
      fetchItems();
    }
  };

  // Pause / Resume Feed
  const handleToggleFeedStatus = async (feed) => {
    const newStatus = feed.status === 'paused' ? 'active' : 'paused';
    await authFetch(API_ENDPOINTS.READER.FEED(feed._id), {
      method: 'PATCH',
      body: JSON.stringify({ status: newStatus }),
    });
    fetchMetadata();
  };

  // Delete Feed (automatically cleans up its articles)
  const handleDeleteFeed = async (feedId) => {
    await authFetch(`${API_ENDPOINTS.READER.FEED(feedId)}?deleteItems=true`, {
      method: 'DELETE',
    });
    if (selectedFeedId === feedId) {
      setSelectedFeedId(null);
    }
    fetchMetadata();
    fetchItems();
  };

  // Update Feed Title
  const handleUpdateFeedTitle = async (feedId, title) => {
    await authFetch(API_ENDPOINTS.READER.FEED(feedId), {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    });
    fetchMetadata();
  };

  // Create Collection
  const handleCreateCollection = async (formData) => {
    const res = await authFetch(API_ENDPOINTS.READER.COLLECTIONS, {
      method: 'POST',
      body: JSON.stringify(formData),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.error?.message || 'Failed to create collection');
    }
    fetchMetadata();
    const colRecord = data.data;
    if (colRecord?._id) {
      setJustCreatedCollectionId(colRecord._id);
    }
    return colRecord || data;
  };

  // OPML Export
  const handleExportOpml = () => {
    window.open(`${API_ENDPOINTS.READER.OPML_EXPORT}?token=${token}`, '_blank');
  };

  // OPML Import
  const handleImportOpml = async (opmlContent) => {
    const res = await authFetch(API_ENDPOINTS.READER.OPML_IMPORT, {
      method: 'POST',
      body: JSON.stringify({ opmlContent }),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.error?.message || 'Failed to import OPML');
    }
    fetchMetadata();
    fetchItems();
    return data.data;
  };

  // Mark all visible items as read
  const handleMarkAllRead = async () => {
    const unread = items.filter((i) => i.readState === 'unread');
    if (unread.length === 0) return;

    setItems((prev) => prev.map((i) => ({ ...i, readState: 'read' })));
    for (const item of unread) {
      authFetch(API_ENDPOINTS.READER.MARK_READ(item._id), {
        method: 'POST',
        body: JSON.stringify({ readState: 'read' }),
      }).catch((e) => console.error(e));
    }
    setTimeout(() => {
      fetchMetadata();
      if (currentView === 'inbox') fetchItems();
    }, 500);
  };

  // Keyboard navigation shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if focus is inside an input or textarea
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) {
        if (e.key === 'Escape') {
          document.activeElement.blur();
        }
        return;
      }

      if (e.key === '/') {
        e.preventDefault();
        const searchInput = document.querySelector('.reader-search-input');
        if (searchInput) searchInput.focus();
      } else if (e.key === 'j') {
        // Next item
        e.preventDefault();
        if (items.length === 0) return;
        const currentIndex = items.findIndex((i) => selectedItem && i._id === selectedItem._id);
        const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        handleSelectItem(items[nextIndex]);
      } else if (e.key === 'k') {
        // Previous item
        e.preventDefault();
        if (items.length === 0) return;
        const currentIndex = items.findIndex((i) => selectedItem && i._id === selectedItem._id);
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        handleSelectItem(items[prevIndex]);
      } else if (e.key === 'm' && selectedItem) {
        // Toggle read
        e.preventDefault();
        handleToggleRead(selectedItem);
      } else if (e.key === 's' && selectedItem) {
        // Toggle favorite
        e.preventDefault();
        handleToggleFavorite(selectedItem);
      } else if (e.key === 'a' && selectedItem) {
        // Toggle archive
        e.preventDefault();
        handleToggleArchive(selectedItem);
      } else if (e.key === 'v' && selectedItem) {
        // Open original URL
        e.preventDefault();
        window.open(selectedItem.originalUrl, '_blank', 'noopener,noreferrer');
      } else if (e.key === 'Escape') {
        if (isMobileReading) {
          setIsMobileReading(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [items, selectedItem, isMobileReading]);

  // View title calculation
  const computedViewTitle = useMemo(() => {
    if (selectedFeedId) {
      const feed = feeds.find((f) => f._id === selectedFeedId);
      return feed ? feed.title : 'Feed';
    }
    if (selectedCollectionId) {
      const col = collections.find((c) => c._id === selectedCollectionId);
      return col ? col.name : 'Collection';
    }
    if (selectedTag) {
      return `#${selectedTag}`;
    }
    if (currentView === 'inbox') return 'Inbox';
    if (currentView === 'favorites') return 'Favorites';
    if (currentView === 'archive') return 'Archive';
    return 'Library';
  }, [currentView, selectedFeedId, selectedCollectionId, selectedTag, feeds, collections]);

  return (
    <div className="reader-workspace">
      <Helmet>
        <title>Reader — RSS & Digital Library | Harivishnu</title>
        <meta
          name="description"
          content="Distraction-free RSS reader and digital library for articles, YouTube videos, podcasts, and saved links."
        />
      </Helmet>

      {/* Top Application Bar */}
      <header className="reader-topbar">
        <div className="reader-topbar-left">
          {/* Mobile drawer hamburger */}
          <button
            className="reader-btn-icon reader-mobile-hamburger"
            onClick={() => setIsMobileDrawerOpen(!isMobileDrawerOpen)}
            title="Toggle navigation"
            aria-label="Toggle navigation drawer"
          >
            <i className="fi fi-rr-menu-burger" />
          </button>

          {/* Desktop collapse toggle */}
          <button
            className="reader-btn-icon reader-desktop-sidebar-toggle"
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            title="Toggle sidebar"
            aria-label="Toggle sidebar"
          >
            <i className="fi fi-rr-sidebar" />
          </button>

          {/* Reader brand logo */}
          <div className="reader-brand-badge">
            <i className="fi fi-rr-book-open-cover" />
            <span>Reader</span>
          </div>
        </div>

        <div className="reader-topbar-actions">
          <button
            className="reader-btn reader-btn-primary reader-action-btn"
            onClick={() => setIsAddLinkOpen(true)}
            title="Add link"
            aria-label="Add link"
          >
            <i className="fi fi-rr-plus" />
            <span className="reader-action-btn-text">Add Link</span>
          </button>

          <button
            className="reader-btn reader-action-btn"
            onClick={() => setIsAddFeedOpen(true)}
            title="Subscribe to RSS"
            aria-label="Subscribe to RSS"
          >
            <i className="fi fi-rr-rss" />
            <span className="reader-action-btn-text">Add Feed</span>
          </button>

          <button
            className="reader-btn-icon"
            onClick={() => setIsSettingsOpen(true)}
            title="Settings & OPML"
            aria-label="Settings and OPML"
          >
            <i className="fi fi-rr-settings" />
          </button>
        </div>
      </header>

      {/* Main 3-Column Workspace */}
      <div className="reader-body">
        {/* Column 1: Sidebar Navigation */}
        <ReaderSidebar
          currentView={currentView}
          selectedFeedId={selectedFeedId}
          selectedCollectionId={selectedCollectionId}
          selectedTag={selectedTag}
          unreadCount={stats.unreadCount || 0}
          totalSaved={stats.totalSaved || 0}
          feeds={feeds}
          collections={collections}
          tags={tags}
          onSelectView={(v) => {
            setCurrentView(v);
            setIsMobileReading(false);
          }}
          onSelectFeed={(id) => {
            setSelectedFeedId(id);
            setIsMobileReading(false);
          }}
          onSelectCollection={(id) => {
            setSelectedCollectionId(id);
            setIsMobileReading(false);
          }}
          onSelectTag={(t) => {
            setSelectedTag(t);
            setIsMobileReading(false);
          }}
          onOpenAddLink={() => setIsAddLinkOpen(true)}
          onOpenAddFeed={() => setIsAddFeedOpen(true)}
          onOpenManageFeeds={() => setIsManageFeedsOpen(true)}
          onOpenSettings={() => setIsSettingsOpen(true)}
          onOpenCreateCollection={() => setIsCreateCollectionOpen(true)}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />

        {/* Mobile Slide-Out Navigation Drawer */}
        {isMobileDrawerOpen && (
          <div
            className="reader-drawer-overlay"
            onClick={() => setIsMobileDrawerOpen(false)}
          >
            <div
              className={`reader-drawer ${isMobileDrawerOpen ? 'open' : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="reader-drawer-header">
                <div className="reader-brand-badge">
                  <i className="fi fi-rr-book-open-cover" />
                  <span>Reader</span>
                </div>
                <button
                  type="button"
                  className="reader-btn-icon reader-drawer-close-btn"
                  onClick={() => setIsMobileDrawerOpen(false)}
                  aria-label="Close navigation menu"
                  title="Close menu"
                >
                  <i className="fi fi-rr-cross-small" style={{ fontSize: '1.25rem' }} />
                </button>
              </div>

              <ReaderSidebar
                inDrawer={true}
                currentView={currentView}
                selectedFeedId={selectedFeedId}
                selectedCollectionId={selectedCollectionId}
                selectedTag={selectedTag}
                unreadCount={stats.unreadCount || 0}
                totalSaved={stats.totalSaved || 0}
                feeds={feeds}
                collections={collections}
                tags={tags}
                onSelectView={(v) => {
                  setCurrentView(v);
                  setIsMobileDrawerOpen(false);
                  setIsMobileReading(false);
                }}
                onSelectFeed={(id) => {
                  setSelectedFeedId(id);
                  setIsMobileDrawerOpen(false);
                  setIsMobileReading(false);
                }}
                onSelectCollection={(id) => {
                  setSelectedCollectionId(id);
                  setIsMobileDrawerOpen(false);
                  setIsMobileReading(false);
                }}
                onSelectTag={(t) => {
                  setSelectedTag(t);
                  setIsMobileDrawerOpen(false);
                  setIsMobileReading(false);
                }}
                onOpenAddLink={() => {
                  setIsMobileDrawerOpen(false);
                  setIsAddLinkOpen(true);
                }}
                onOpenAddFeed={() => {
                  setIsMobileDrawerOpen(false);
                  setIsAddFeedOpen(true);
                }}
                onOpenManageFeeds={() => {
                  setIsMobileDrawerOpen(false);
                  setIsManageFeedsOpen(true);
                }}
                onOpenSettings={() => {
                  setIsMobileDrawerOpen(false);
                  setIsSettingsOpen(true);
                }}
                onOpenCreateCollection={() => {
                  setIsMobileDrawerOpen(false);
                  setIsCreateCollectionOpen(true);
                }}
              />
            </div>
          </div>
        )}

        {/* Column 2: Content List (Hidden on mobile if user is reading an article) */}
        {(!isMobileReading || window.innerWidth >= 640) && (
          <ReaderContentList
            items={items}
            selectedItem={selectedItem}
            onSelectItem={handleSelectItem}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedFilter={selectedFilter}
            onFilterChange={setSelectedFilter}
            sortOrder={sortOrder}
            onSortChange={setSortOrder}
            onMarkAllRead={handleMarkAllRead}
            isLoading={isLoading}
            viewTitle={computedViewTitle}
            collections={collections}
            onEditItem={handleOpenEditLink}
            onToggleRead={handleToggleRead}
            onDismissItem={handleDismissItem}
            onDeleteItem={handleDeleteItem}
            onDeleteBatch={handleDeleteBatch}
            onRefreshFeed={handleRefreshCurrentFeed}
          />
        )}

        {/* Column 3: Reading Pane (Full screen on mobile when isMobileReading is true) */}
        {(isMobileReading || window.innerWidth >= 640) && (
          <ReaderDetailPane
            item={selectedItem}
            collections={collections}
            onBack={() => setIsMobileReading(false)}
            onToggleRead={handleToggleRead}
            onToggleFavorite={handleToggleFavorite}
            onToggleArchive={handleToggleArchive}
            onDelete={handleDeleteItem}
            onRefreshItem={handleRefreshItem}
            onEdit={handleOpenEditLink}
            onUpdateNote={handleUpdateNote}
            onUpdateTags={handleUpdateTags}
            onUpdateCollections={handleUpdateCollections}
            showBackButton={isMobileReading && window.innerWidth < 1024}
          />
        )}
      </div>

      {/* Modals */}
      <AddLinkModal
        isOpen={isAddLinkOpen}
        onClose={() => setIsAddLinkOpen(false)}
        onSubmit={handleSaveLink}
        collections={collections}
        defaultCollectionId={selectedCollectionId}
        onOpenCreateCollection={() => setIsCreateCollectionOpen(true)}
        onCreateCollection={handleCreateCollection}
        justCreatedCollectionId={justCreatedCollectionId}
      />

      <EditLinkModal
        isOpen={isEditLinkOpen}
        onClose={() => {
          setIsEditLinkOpen(false);
          setEditingItem(null);
        }}
        item={editingItem}
        collections={collections}
        onSave={handleSaveEditLink}
        onOpenCreateCollection={() => setIsCreateCollectionOpen(true)}
        onCreateCollection={handleCreateCollection}
        justCreatedCollectionId={justCreatedCollectionId}
      />

      <AddFeedModal
        isOpen={isAddFeedOpen}
        onClose={() => setIsAddFeedOpen(false)}
        onSubscribe={handleSubscribeFeed}
        onDiscover={handleDiscoverFeeds}
        collections={collections}
        defaultCollectionId={selectedCollectionId}
        onOpenCreateCollection={() => setIsCreateCollectionOpen(true)}
        onCreateCollection={handleCreateCollection}
        justCreatedCollectionId={justCreatedCollectionId}
      />

      <ManageFeedsModal
        isOpen={isManageFeedsOpen}
        onClose={() => setIsManageFeedsOpen(false)}
        feeds={feeds}
        collections={collections}
        onRefreshFeed={handleRefreshFeed}
        onToggleStatus={handleToggleFeedStatus}
        onDeleteFeed={handleDeleteFeed}
        onUpdateTitle={handleUpdateFeedTitle}
        onUpdateCollections={handleUpdateFeedCollections}
      />

      <CreateCollectionModal
        isOpen={isCreateCollectionOpen}
        onClose={() => setIsCreateCollectionOpen(false)}
        onCreate={handleCreateCollection}
        onSuccess={(col) => {
          const colId = col?._id || col?.data?._id;
          if (colId) setJustCreatedCollectionId(colId);
        }}
      />

      <ReaderSettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        onExportOpml={handleExportOpml}
        onImportOpml={handleImportOpml}
        autoDeleteSettings={autoDeleteSettings}
        onUpdateAutoDeleteSettings={handleUpdateAutoDeleteSettings}
        onRunAutoDeleteNow={handleRunAutoDelete}
      />
    </div>
  );
}
